// src/canvas/ExplorerAssessmentCanvas.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchExplorerRisks } from '../redux/slices/risksSlice';
import { fetchExplorerEvaluations } from '../redux/slices/riskEvaluationsSlice';
import { fetchResidualRisks } from '../redux/slices/residualRiskSlice';
import { setSavedControls } from '../redux/actions/controlActions';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container, Stack, Group, Title, Text, Badge, Tabs, Card, Grid, Alert, Button, Paper, ActionIcon
} from '@mantine/core';
import {
  IconChartBar, IconClipboardList, IconTarget, IconRadar, IconCompass, IconAlertCircle,
  IconBuildingBank, IconArrowLeft, IconExternalLink, IconShield
} from '@tabler/icons-react';
import ExplorerChartsSummary from './ExplorerChartsSummary';
import ExplorerRiskAreaCard from './ExplorerRiskAreaCard';
import ActionPlan from '../legacy/ActionPlan';
import Monitors from '../Monitors';
import RiskManagerToolkit from '../RiskManagerToolkit';
import { isRiskManager } from '../utils/roleHelpers';
import { selectAssessmentById, initializeAssessmentLocal } from '../redux/slices/assessmentsSlice';
import { selectExplorerRisksByArea, selectInheritedBuilderRisks } from '../redux/slices/risksSlice';
import { jwtDecode } from 'jwt-decode';
import { authFetch } from '../utils/authFetch';

// Priority color mapping (consistent with RiskLandscape and SectionAssessmentCardV2)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' }
};

// Fallback Explorer risk areas (used when template config is not available)
const EXPLORER_RISK_AREAS_FALLBACK = {
  commercialRisk: { key: 'commercialRisk', label: 'Commercial Risk', description: 'Revenue, market position, customer trust', color: 'blue' },
  operationalRisk: { key: 'operationalRisk', label: 'Operational Risk', description: 'Service delivery, processes, dependencies', color: 'orange' },
  complianceRisk: { key: 'complianceRisk', label: 'Compliance & Legal', description: 'Regulatory requirements, legal liability', color: 'red' },
  societalRisk: { key: 'societalRisk', label: 'Societal & Ethical', description: 'Social impact, ethics, human rights', color: 'violet' },
  environmentalRisk: { key: 'environmentalRisk', label: 'Environmental', description: 'Sustainability, carbon footprint, ESG', color: 'green' },
  reputationalRisk: { key: 'reputationalRisk', label: 'Reputational', description: 'Stakeholder trust, brand integrity', color: 'cyan' }
};

/**
 * ExplorerAssessmentCanvas - Report/Canvas view for Explorer assessments
 * Matches Builder's AssessmentCanvasV2 UI/UX but shows organizational risk areas
 */
export default function ExplorerAssessmentCanvas() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useParams();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [userIsRiskManager, setUserIsRiskManager] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedArea, setExpandedArea] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Redux state
  const assessment = useSelector(state => selectAssessmentById(state, assessmentUuid));
  const modelsById = useSelector(state => state.models?.byId || {});
  const model = modelsById[modelId];
  const currentUser = useSelector(state => state.app?.currentUser);

  // Explorer risks from Redux (grouped by area)
  const explorerRisks = useSelector(state => selectExplorerRisksByArea(state, assessmentUuid));

  // Saved controls from Redux (keyed by riskId)
  const savedControls = useSelector(state => state.controls?.savedControls || {});

  // Residual risks from Redux
  const residualRisks = useSelector(state => state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {});

  // Get parent (Builder) assessment if linked
  const parentAssessmentUuid = assessment?.parent_assessment_uuid || assessment?.info?.parent_assessment_uuid;
  const parentAssessment = useSelector(state =>
    parentAssessmentUuid ? selectAssessmentById(state, parentAssessmentUuid) : null
  );

  // Inherited Builder risks from parent assessment (modelRisks)
  const inheritedModelRisks = useSelector(state => selectInheritedBuilderRisks(state, parentAssessmentUuid));

  // Inherited security scenarios from parent assessment
  // Note: Some scenario properties may be objects - convert to strings safely
  const inheritedSecurityScenarios = useSelector(state => {
    if (!parentAssessmentUuid) return [];
    const scenarios = state.security?.byAssessment?.[parentAssessmentUuid]?.scenarios || {};
    return Object.values(scenarios).map(scenario => {
      // Helper to safely convert value to string (handles objects)
      const toString = (val, fallback = '') => {
        if (val === null || val === undefined) return fallback;
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
          // If object, join keys or convert to readable string
          return Object.keys(val).join(', ') || fallback;
        }
        return String(val);
      };

      return {
        riskId: scenario.scenarioId || scenario.id || `scenario_${Math.random().toString(36).slice(2)}`,
        riskName: toString(scenario.name, toString(scenario.scenarioName, 'Unnamed Scenario')),
        priority: toString(scenario.impact, toString(scenario.priority, 'Moderate')),
        category: toString(scenario.attackSurface, 'Security'),
        sectionName: 'Security Assessment',
        status: toString(scenario.status, 'Saved'),
        rationale: toString(scenario.rationale, ''),
        type: 'SecurityScenario'
      };
    });
  });

  // Combine all inherited risks (model risks + security scenarios)
  const inheritedRisks = useMemo(() => {
    return [...inheritedModelRisks, ...inheritedSecurityScenarios];
  }, [inheritedModelRisks, inheritedSecurityScenarios]);

  // Group inherited risks by section for display
  const inheritedRisksBySection = useMemo(() => {
    const bySection = {};
    inheritedRisks.forEach(risk => {
      const section = risk.sectionName || 'Other';
      if (!bySection[section]) {
        bySection[section] = [];
      }
      bySection[section].push(risk);
    });
    return bySection;
  }, [inheritedRisks]);

  // Risk areas from template config, with fallback to defaults
  const riskAreas = useMemo(() => {
    const templateAreas = assessment?.templateConfig?.taxonomy?.riskAreas;
    if (templateAreas && typeof templateAreas === 'object') {
      const areas = {};
      const colors = ['blue', 'orange', 'red', 'violet', 'green', 'cyan', 'teal', 'pink'];
      Object.keys(templateAreas).forEach((key, idx) => {
        const area = templateAreas[key];
        areas[key] = {
          key,
          label: area.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
          description: area.description || '',
          color: area.color || colors[idx % colors.length]
        };
      });
      return areas;
    }
    return EXPLORER_RISK_AREAS_FALLBACK;
  }, [assessment?.templateConfig?.taxonomy?.riskAreas]);

  // Fetch template config if not loaded
  useEffect(() => {
    const templateId = assessment?.info?.template_id;
    if (templateId && !assessment?.templateConfig) {
      const fetchTemplate = async () => {
        try {
          const resp = await authFetch('/.netlify/functions/templates-getTemplate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: templateId })
          });
          if (resp.ok) {
            const template = await resp.json();
            dispatch(initializeAssessmentLocal({
              assessmentUuid,
              modelId,
              templateId,
              templateConfig: template,
            }));
          }
        } catch (err) {
          console.warn('[ExplorerAssessmentCanvas] Failed to fetch template config:', err);
        }
      };
      fetchTemplate();
    }
  }, [assessment?.info?.template_id, assessment?.templateConfig, assessmentUuid, modelId, dispatch]);

  // Load Explorer data on mount: risks, evaluations, controls, residual risks
  useEffect(() => {
    let isMounted = true;

    const loadExplorerData = async () => {
      if (!assessmentUuid || !modelId) return;

      console.log('[ExplorerAssessmentCanvas] Loading Explorer data (risks, evaluations, controls, residual)...');
      try {
        // Load all explorer data in parallel
        await Promise.all([
          dispatch(fetchExplorerRisks({ assessmentUuid, modelId })),
          dispatch(fetchExplorerEvaluations({ modelUuid: modelId, assessmentUuid })),
          dispatch(setSavedControls(assessmentUuid, modelId)),
          dispatch(fetchResidualRisks({ assessmentUuid, modelId }))
        ]);

        if (isMounted) {
          console.log('[ExplorerAssessmentCanvas] All Explorer data loaded successfully');
          setDataLoaded(true);
        }
      } catch (error) {
        console.error('[ExplorerAssessmentCanvas] Error loading Explorer data:', error);
        // Still mark as loaded to prevent infinite retries
        if (isMounted) {
          setDataLoaded(true);
        }
      }
    };

    // Only load if not already loaded for this assessment
    if (!dataLoaded) {
      loadExplorerData();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentUuid, modelId]);

  // Check Risk Manager role
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'openid profile email read:models write:models delete:models'
          }
        });
        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserIsRiskManager(isRiskManager(roles));
      } catch (error) {
        console.error('[ExplorerAssessmentCanvas] Error fetching roles:', error);
        setUserIsRiskManager(false);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  // Determine if user can edit
  const canEdit = useMemo(() => {
    const isOwner = currentUser?.email === assessment?.owner_email ||
                    currentUser?.email === assessment?.info?.owner_email;
    return isOwner || userIsRiskManager;
  }, [currentUser, assessment, userIsRiskManager]);

  // Calculate stats for each area
  const areaStats = useMemo(() => {
    const stats = {};
    let totalRisks = 0;
    let totalActions = 0;
    let completedActions = 0;
    const priorityCounts = { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 };

    Object.keys(riskAreas).forEach(areaKey => {
      const areaRisks = explorerRisks?.[areaKey] || [];
      const riskCount = areaRisks.length;

      // Count actions from savedControls (keyed by riskId)
      let actionCount = 0;
      let completed = 0;
      areaRisks.forEach(risk => {
        const riskControls = savedControls[risk.riskId] || [];
        // Filter controls for this model
        const modelControls = riskControls.filter(c => c.modelId === modelId);
        actionCount += modelControls.length;
        completed += modelControls.filter(c => c.status === 'completed').length;
      });

      // Count priorities (normalize to proper case)
      const areaPriorities = { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 };
      areaRisks.forEach(risk => {
        const rawPriority = risk.priority || risk.evaluation?.priority || 'Moderate';
        // Normalize priority to proper case (e.g., "moderate" -> "Moderate")
        const priority = rawPriority.charAt(0).toUpperCase() + rawPriority.slice(1).toLowerCase();
        if (areaPriorities[priority] !== undefined) {
          areaPriorities[priority]++;
          priorityCounts[priority]++;
        }
      });

      stats[areaKey] = {
        riskCount,
        actionCount,
        completedActions: completed,
        progress: actionCount > 0 ? Math.round((completed / actionCount) * 100) : 0,
        priorities: areaPriorities
      };

      totalRisks += riskCount;
      totalActions += actionCount;
      completedActions += completed;
    });

    return {
      byArea: stats,
      totals: {
        risks: totalRisks,
        actions: totalActions,
        completedActions,
        progress: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0,
        priorities: priorityCounts
      }
    };
  }, [explorerRisks, savedControls, modelId, riskAreas]);

  // Calculate AI scores (simplified for explorer)
  const aiScores = useMemo(() => {
    const { totals } = areaStats;
    const { priorities, risks } = totals;

    // Risk Severity Score (0-100)
    const severityScore = risks > 0
      ? Math.min(100, Math.round(
          ((priorities.Critical * 100) + (priorities.High * 80) + (priorities.Significant * 60) +
           (priorities.Moderate * 40) + (priorities.Low * 20) + (priorities.Negligible * 10)) / risks
        ))
      : 0;

    const severityLevel = severityScore >= 70 ? 'HIGH' : severityScore >= 40 ? 'MEDIUM' : 'LOW';

    // Mitigation Quality Score - factor in residual risk ratings and action plan ratings
    let mitigationScore = totals.progress;
    const residualRiskEntries = Object.entries(residualRisks).filter(([key]) => key !== 'loading' && key !== 'error');
    if (residualRiskEntries.length > 0) {
      // Calculate average residual risk quality based on action_plan_rating
      const ratingScores = { 'Excellent': 100, 'Very Good': 85, 'Good': 70, 'Fair': 50, 'Poor': 30, 'Unsatisfactory': 10 };
      let totalRatingScore = 0;
      let ratedCount = 0;
      residualRiskEntries.forEach(([, data]) => {
        if (data?.action_plan_rating && ratingScores[data.action_plan_rating]) {
          totalRatingScore += ratingScores[data.action_plan_rating];
          ratedCount++;
        }
      });
      if (ratedCount > 0) {
        // Blend action completion progress with action plan quality ratings
        const avgRatingScore = totalRatingScore / ratedCount;
        mitigationScore = Math.round((totals.progress * 0.4) + (avgRatingScore * 0.6));
      }
    }
    const mitigationLevel = mitigationScore >= 70 ? 'STRONG' : mitigationScore >= 40 ? 'MODERATE' : 'WEAK';

    // Business Impact (based on critical + high weighted)
    const impactScore = risks > 0
      ? Math.min(100, Math.round(
          ((priorities.Critical * 100) + (priorities.High * 75) + (priorities.Significant * 50)) / risks
        ))
      : 0;
    const impactLevel = impactScore >= 60 ? 'HIGH' : impactScore >= 30 ? 'MEDIUM' : 'LOW';

    return {
      severity: { score: severityScore, level: severityLevel },
      mitigation: { score: mitigationScore, level: mitigationLevel },
      impact: { score: impactScore, level: impactLevel }
    };
  }, [areaStats, residualRisks]);

  // Assessment info
  const assessmentName = assessment?.assessment_name || assessment?.info?.assessment_name || 'Explorer Assessment';
  const modelName = model?.model_name || 'Unknown Model';
  const assessmentStatus = assessment?.status || assessment?.info?.status || 'in_progress';

  if (!assessment) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" icon={<IconAlertCircle size={20} />}>
          Assessment not found. Please ensure the assessment exists and you have access.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Card withBorder shadow="sm" p="md">
          <Stack gap="md">
            {/* Back button and title */}
            <Group justify="space-between" align="flex-start">
              <Group gap="md">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => navigate('/risk-reports')}
                  aria-label="Back to assessments"
                >
                  <IconArrowLeft size={20} />
                </ActionIcon>
                <Stack gap={4}>
                  <Group gap="sm">
                    <IconCompass size={24} color="var(--mantine-color-green-6)" />
                    <Title order={2}>{assessmentName}</Title>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">Model: {modelName}</Text>
                    <Text size="sm" c="dimmed">•</Text>
                    <Badge color="green" variant="light" leftSection={<IconCompass size={12} />}>
                      Explorer
                    </Badge>
                    <Badge
                      color={assessmentStatus === 'completed' ? 'green' : 'blue'}
                      variant="light"
                    >
                      {assessmentStatus === 'completed' ? 'Completed' : 'In Progress'}
                    </Badge>
                  </Group>
                </Stack>
              </Group>

              {/* Parent Builder link */}
              {parentAssessmentUuid && parentAssessment && (
                <Paper withBorder p="sm" bg="blue.0">
                  <Group gap="sm">
                    <Text size="xs" c="dimmed">Linked Builder:</Text>
                    <Button
                      size="xs"
                      variant="subtle"
                      rightSection={<IconExternalLink size={12} />}
                      onClick={() => navigate(`/assessment-report/${modelId}?assessmentUuid=${parentAssessmentUuid}`)}
                    >
                      {parentAssessment?.assessment_name || parentAssessment?.info?.assessment_name || 'Builder Assessment'}
                    </Button>
                  </Group>
                </Paper>
              )}
            </Group>

            {/* AI Score Cards */}
            <Grid>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>RISK SEVERITY</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.severity.level === 'HIGH' ? 'red' :
                      aiScores.severity.level === 'MEDIUM' ? 'orange' : 'green'
                    }>
                      {aiScores.severity.score}
                    </Text>
                    <Badge
                      color={aiScores.severity.level === 'HIGH' ? 'red' :
                             aiScores.severity.level === 'MEDIUM' ? 'orange' : 'green'}
                      variant="light"
                    >
                      {aiScores.severity.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>MITIGATION QUALITY</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.mitigation.level === 'STRONG' ? 'green' :
                      aiScores.mitigation.level === 'MODERATE' ? 'orange' : 'red'
                    }>
                      {aiScores.mitigation.score}%
                    </Text>
                    <Badge
                      color={aiScores.mitigation.level === 'STRONG' ? 'green' :
                             aiScores.mitigation.level === 'MODERATE' ? 'orange' : 'red'}
                      variant="light"
                    >
                      {aiScores.mitigation.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>BUSINESS IMPACT</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.impact.level === 'HIGH' ? 'red' :
                      aiScores.impact.level === 'MEDIUM' ? 'orange' : 'green'
                    }>
                      {aiScores.impact.score}
                    </Text>
                    <Badge
                      color={aiScores.impact.level === 'HIGH' ? 'red' :
                             aiScores.impact.level === 'MEDIUM' ? 'orange' : 'green'}
                      variant="light"
                    >
                      {aiScores.impact.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="risks" leftSection={<IconBuildingBank size={16} />}>
              Enterprise Risks
            </Tabs.Tab>
            {parentAssessmentUuid && (
              <Tabs.Tab value="inherited" leftSection={<IconClipboardList size={16} />}>
                Technical Risks
              </Tabs.Tab>
            )}
            <Tabs.Tab value="actionPlan" leftSection={<IconTarget size={16} />}>
              Action Plan
            </Tabs.Tab>
            <Tabs.Tab value="monitors" leftSection={<IconRadar size={16} />}>
              Monitors
            </Tabs.Tab>
            {userIsRiskManager && assessmentStatus === 'completed' && (
              <Tabs.Tab value="toolkit" leftSection={<IconShield size={16} />}>
                Risk Manager Toolkit
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="md">
            <ExplorerChartsSummary
              areaStats={areaStats}
              explorerRisks={explorerRisks}
              riskAreas={riskAreas}
              savedControls={savedControls}
              modelId={modelId}
            />
          </Tabs.Panel>

          {/* Enterprise Risks Tab */}
          <Tabs.Panel value="risks" pt="md">
            <Stack gap="md">
              {/* Instructions for better UX */}
              <Card withBorder radius="md" p="md" bg="blue.0">
                <Text size="sm" c="blue.7">
                  💡 <strong>Tip:</strong> Click on any risk area card to expand and see detailed risks and actions.
                  Cards will expand to full width for better readability.
                </Text>
              </Card>

              {/* Dynamic Grid Layout */}
              {expandedArea ? (
                // When an area is expanded, show it full width with others minimized
                <Stack gap="md">
                  {/* Minimized other areas */}
                  <Grid gutter="md">
                    {Object.entries(riskAreas)
                      .filter(([areaKey]) => areaKey !== expandedArea)
                      .map(([areaKey, areaConfig]) => (
                        <Grid.Col span={6} key={`minimized-${areaKey}`}>
                          <ExplorerRiskAreaCard
                            areaKey={areaKey}
                            areaConfig={areaConfig}
                            risks={explorerRisks?.[areaKey] || []}
                            stats={areaStats.byArea[areaKey]}
                            isExpanded={false}
                            onToggleExpand={() => setExpandedArea(areaKey)}
                            canEdit={canEdit}
                            modelId={modelId}
                            assessmentUuid={assessmentUuid}
                            isMinimized={true}
                          />
                        </Grid.Col>
                      ))}
                  </Grid>

                  {/* Expanded area - full width */}
                  <ExplorerRiskAreaCard
                    areaKey={expandedArea}
                    areaConfig={riskAreas[expandedArea]}
                    risks={explorerRisks?.[expandedArea] || []}
                    stats={areaStats.byArea[expandedArea]}
                    isExpanded={true}
                    onToggleExpand={() => setExpandedArea(null)}
                    canEdit={canEdit}
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    isMinimized={false}
                  />
                </Stack>
              ) : (
                // Default 2-column layout when no area is expanded
                <Grid gutter="lg">
                  {Object.entries(riskAreas).map(([areaKey, areaConfig]) => (
                    <Grid.Col span={{ base: 12, md: 6 }} key={areaKey}>
                      <ExplorerRiskAreaCard
                        areaKey={areaKey}
                        areaConfig={areaConfig}
                        risks={explorerRisks?.[areaKey] || []}
                        stats={areaStats.byArea[areaKey]}
                        isExpanded={false}
                        onToggleExpand={() => setExpandedArea(areaKey)}
                        canEdit={canEdit}
                        modelId={modelId}
                        assessmentUuid={assessmentUuid}
                        isMinimized={false}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Inherited Risks Tab */}
          {parentAssessmentUuid && (
            <Tabs.Panel value="inherited" pt="md">
              <Stack gap="md">
                <Paper withBorder p="md" bg="blue.0">
                  <Group justify="space-between">
                    <Stack gap="xs">
                      <Text fw={600}>From Builder Assessment</Text>
                      <Text size="sm" c="dimmed">
                        {parentAssessment?.assessment_name || parentAssessment?.info?.assessment_name || 'Builder Assessment'}
                      </Text>
                    </Stack>
                    <Button
                      variant="light"
                      rightSection={<IconExternalLink size={14} />}
                      onClick={() => navigate(`/assessment-report/${modelId}?assessmentUuid=${parentAssessmentUuid}`)}
                    >
                      View Full Report
                    </Button>
                  </Group>
                </Paper>
                <Alert color="gray" variant="light">
                  <Text size="sm">
                    These technical risks from the Builder assessment informed the organizational risk analysis above.
                    View the full Builder assessment for details on Safety & Alignment risks and Security threats.
                  </Text>
                </Alert>

                {/* Display inherited Builder risks by section */}
                {Object.keys(inheritedRisksBySection).length === 0 ? (
                  <Card withBorder p="md">
                    <Text c="dimmed" ta="center">
                      No inherited risks found from Builder assessment.
                    </Text>
                  </Card>
                ) : (
                  <Stack gap="md">
                    {Object.entries(inheritedRisksBySection).map(([sectionName, risks]) => (
                      <Card key={sectionName} withBorder p="md">
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Text fw={600}>{sectionName.replace(/([a-z])([A-Z])/g, '$1 $2')}</Text>
                            <Badge variant="light" color="blue">{risks.length} risks</Badge>
                          </Group>
                          <Stack gap="xs">
                            {risks.map(risk => {
                              const priorityConfig = PRIORITY_COLORS[risk.priority] || { bg: '#e9ecef', text: '#495057' };
                              return (
                                <Paper key={risk.riskId} withBorder p="sm" bg="gray.0">
                                  <Group justify="space-between" align="flex-start">
                                    <Stack gap={2} style={{ flex: 1 }}>
                                      <Text size="sm">{risk.riskName}</Text>
                                      <Text size="xs" c="dimmed">{risk.category}</Text>
                                    </Stack>
                                    {risk.priority && (
                                      <Badge
                                        size="sm"
                                        style={{
                                          backgroundColor: priorityConfig.bg,
                                          color: priorityConfig.text
                                        }}
                                      >
                                        {risk.priority}
                                      </Badge>
                                    )}
                                  </Group>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Tabs.Panel>
          )}

          {/* Action Plan Tab */}
          <Tabs.Panel value="actionPlan" pt="md">
            <ActionPlan
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              isExplorer={true}
            />
          </Tabs.Panel>

          {/* Monitors Tab */}
          <Tabs.Panel value="monitors" pt="md">
            <Monitors modelId={modelId} />
          </Tabs.Panel>

          {/* Risk Manager Toolkit Tab */}
          {userIsRiskManager && assessmentStatus === 'completed' && (
            <Tabs.Panel value="toolkit" pt="md">
              <RiskManagerToolkit
                modelId={modelId}
                assessmentUuid={assessmentUuid}
                riskFormData={explorerRisks}
                narratives={{}}
                isExplorer={true}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>
    </Container>
  );
}
